/**
 * UI-PALETTE-1 — the shared shell palette, defined once (ADR-027 Utils).
 *
 * Every value below is read from shipped source, not from the mockup PNG:
 * the owner approved the colours Valuation step 5 already renders (Your value:
 * Compare values and the 10-year Forecast). palette.test.ts re-reads those
 * files and fails if a literal here drifts from them.
 *
 * Both Tailwind configs emit these as `--pal-*` CSS variables on :root
 * (shellCssVars), and each product's index.css points its role variables
 * (--primary, --sidebar, --card ...) at them. Charts, status colours and
 * report PDFs do not use this file.
 */

export const PALETTE = {
  // ── Step-5 values ──────────────────────────────────────────────────────
  /** _ValueForecast.tsx NAVY — headings, navy tile, sidebar. */
  navy: '#0d1f3c',
  /** _ValueForecast.tsx GOLD — gold tile, accents. */
  gold: '#c9a24a',
  /** _ValueForecast.tsx gold-tile gradient end (linear-gradient(135deg, GOLD, #ecd089)). */
  goldLight: '#ecd089',
  /** _ValueForecast.tsx BLUE — blue tile. */
  blue: '#3b6fb6',
  /** _valueMethods.ts asset colour. Kept as a token; never under text (3.91:1 with white). */
  teal: '#2f8f83',
  /** Step-5 section surface (bg-white). */
  white: '#ffffff',
  /** Step-5 section border (border-gray-200) and chart grid. */
  line: '#e5e7eb',
  /** Step-5 value text (text-gray-900). */
  ink: '#111827',
  /** Step-5 note text (text-gray-600). */
  inkSoft: '#4b5563',
  /** The page step 5 renders on: ValuationShell var(--background), index.css. */
  page: '#f6f7f9',
  // ── Shell extras: existing tokens or exact derivations, not step-5 literals ──
  /** index.css --sidebar-accent: the raised (active / hover) row on the navy rail. */
  navyRaised: '#1c3252',
  /** White at 70% over navy, as a solid colour — AppShell's inactive nav link. */
  navyMuted: '#b6bcc5',
  /** GOLD at 0x33 alpha over white, as a solid colour — the step-5 rate pill. */
  goldSoft: '#f4ecdb',
} as const

export type PaletteName = keyof typeof PALETTE

/** CSS variable name for a token: goldLight -> --pal-gold-light. */
export function paletteVar(name: PaletteName): string {
  return `--pal-${name.replace(/[A-Z]/g, c => `-${c.toLowerCase()}`)}`
}

function parseHex(hex: string): [number, number, number] {
  let h = hex.trim().replace(/^#/, '')
  if (h.length === 3) h = h.split('').map(c => c + c).join('')
  if (!/^[0-9a-fA-F]{6}$/.test(h)) throw new Error(`not a 3- or 6-digit hex colour: ${hex}`)
  return [0, 2, 4].map(i => parseInt(h.slice(i, i + 2), 16)) as [number, number, number]
}

/** "h s% l%" — the triplet form OTCID's shadcn vars use inside hsl(var(--x)). */
export function hexToHslTriplet(hex: string): string {
  const [r, g, b] = parseHex(hex).map(v => v / 255)
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const l = (max + min) / 2
  let h = 0
  let s = 0
  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0)
    else if (max === g) h = (b - r) / d + 2
    else h = (r - g) / d + 4
    h *= 60
  }
  const round1 = (n: number) => Math.round(n * 10) / 10
  return `${round1(h)} ${round1(s * 100)}% ${round1(l * 100)}%`
}

/** The :root block both Tailwind configs inject: --pal-<name> (hex) and --pal-<name>-hsl. */
export function shellCssVars(): Record<string, string> {
  const vars: Record<string, string> = {}
  for (const name of Object.keys(PALETTE) as PaletteName[]) {
    vars[paletteVar(name)] = PALETTE[name]
    vars[`${paletteVar(name)}-hsl`] = hexToHslTriplet(PALETTE[name])
  }
  return vars
}

/** Dashboard tiles, in order. The gold tile is a gradient; text is checked against both ends. */
export const TILE_TONES = [
  { name: 'navy', background: PALETTE.navy, text: PALETTE.white, ends: [PALETTE.navy] },
  {
    name: 'gold',
    background: `linear-gradient(135deg, ${PALETTE.gold}, ${PALETTE.goldLight})`,
    text: PALETTE.navy,
    ends: [PALETTE.gold, PALETTE.goldLight],
  },
  { name: 'blue', background: PALETTE.blue, text: PALETTE.white, ends: [PALETTE.blue] },
  { name: 'white', background: PALETTE.white, text: PALETTE.ink, ends: [PALETTE.white] },
] as const

/**
 * Inline style for the tile at `index` (tones cycle). backgroundColor is the
 * solid first end, so a renderer that drops the gradient still shows the tone.
 */
export function tileStyle(index: number): Record<string, string> {
  const t = TILE_TONES[index % TILE_TONES.length]
  return {
    backgroundColor: t.ends[0],
    ...(t.ends.length > 1 ? { backgroundImage: t.background } : {}),
    color: t.text,
    borderColor: t.name === 'white' ? PALETTE.line : t.ends[0],
  }
}

// ── WCAG 2.1 contrast ────────────────────────────────────────────────────

function channel(v: number): number {
  const s = v / 255
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4
}

export function relativeLuminance(hex: string): number {
  const [r, g, b] = parseHex(hex)
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b)
}

/** WCAG contrast ratio of two opaque hex colours, 1..21. */
export function contrastRatio(a: string, b: string): number {
  const [hi, lo] = [relativeLuminance(a), relativeLuminance(b)].sort((x, y) => y - x)
  return (hi + 0.05) / (lo + 0.05)
}

/** WCAG AA: 4.5:1 body text, 3:1 large text. */
export const AA_BODY = 4.5
export const AA_LARGE = 3

/**
 * Every text-on-background pair the shell draws from this palette. All are
 * body-size checks (4.5:1) — none relies on the large-text allowance.
 */
export const TEXT_PAIRS: ReadonlyArray<{ use: string; text: string; background: string; large?: boolean }> = [
  { use: 'Sidebar text and active link', text: PALETTE.white, background: PALETTE.navy },
  { use: 'Sidebar inactive link and labels', text: PALETTE.navyMuted, background: PALETTE.navy },
  { use: 'Sidebar active / hover row', text: PALETTE.white, background: PALETTE.navyRaised },
  { use: 'Sidebar mark and avatar', text: PALETTE.navy, background: PALETTE.gold },
  { use: 'Page heading on page', text: PALETTE.ink, background: PALETTE.page },
  { use: 'Page note on page', text: PALETTE.inkSoft, background: PALETTE.page },
  { use: 'Header title on white', text: PALETTE.navy, background: PALETTE.white },
  { use: 'Header breadcrumb on white', text: PALETTE.inkSoft, background: PALETTE.white },
  { use: 'Card text on white', text: PALETTE.ink, background: PALETTE.white },
  { use: 'Primary button', text: PALETTE.white, background: PALETTE.navy },
  { use: 'Secondary button', text: PALETTE.navy, background: PALETTE.goldSoft },
  ...TILE_TONES.flatMap(t =>
    t.ends.map(end => ({ use: `${t.name} tile`, text: t.text, background: end })),
  ),
]
